import { get } from '../utils/request';

/**
 * 工作台统计数据接口
 */
export interface DashboardStatistics {
  lead_statistics: {
    total_leads: number;
    my_leads: number;
    public_pool_leads: number;
    converted_leads: number;
    conversion_rate: number;
  };
  case_statistics: {
    total_cases: number;
    active_cases: number;
    closed_cases: number;
    my_cases: number;
    as_responsible: number;
    as_assistant: number;
  };
  finance_statistics: {
    total_records: number;
    total_amount: number;
    lead_contract_amount: number;      // 线索合同总额
    lead_received_amount: number;      // 线索已收款总额
    case_contract_amount: number;      // 案件合同总额
    case_received_amount: number;      // 案件已收款总额
  };
  follow_up_statistics: {
    total_follow_ups: number;
    pending_follow_ups: number;
    completed_follow_ups: number;
  };
  recent_activities: {
    recent_leads: number;
    recent_cases: number;
    recent_follow_ups: number;
  };
}

/**
 * 获取工作台统计数据
 */
export const getDashboardStatistics = async (): Promise<DashboardStatistics> => {
  return await get<DashboardStatistics>('/statistics/dashboard');
};

/**
 * 线索转化趋势数据接口
 */
export interface LeadTrendData {
  dates: string[];              // 日期数组，格式：MM-DD
  new_leads: number[];          // 每日新增线索数
  converted_leads: number[];    // 每日转化线索数
  conversion_rates: number[];   // 每日转化率(%)
}

/**
 * 获取线索转化趋势数据
 * @param days 统计天数，默认7天
 */
export const getLeadTrend = async (days: number = 7): Promise<LeadTrendData> => {
  return await get<LeadTrendData>(`/statistics/lead-trend?days=${days}`);
};

/**
 * 个人线索跟进情况数据接口（折线图）
 */
export interface PersonalLeadDistributionData {
  dates: string[];                  // 日期数组
  employees: string[];              // 员工姓名数组
  data: Record<string, number[]>;   // 每个员工每天的跟进线索数 { "员工名": [day1, day2, ...] }
}

/**
 * 获取个人线索跟进情况数据
 */
export const getPersonalLeadDistribution = async (days: number = 7): Promise<PersonalLeadDistributionData> => {
  return await get<PersonalLeadDistributionData>('/statistics/personal-lead-distribution', { params: { days } });
};

/**
 * 线索意向分布数据接口
 */
export interface LeadIntentionDistributionData {
  employees: string[];                           // 员工姓名数组
  intentions: string[];                          // 跟进意向类型数组
  data: Record<string, number>[];                // 每个员工的意向分布数据
}

/**
 * 线索意向分布筛选参数
 */
export interface LeadIntentionDistributionParams {
  start_date?: string;      // 认领开始日期
  end_date?: string;        // 认领结束日期
  assigned_to?: number;     // 负责人ID
  data_source?: string;     // 数据来源
  lead_type?: string;       // 线索类型
}

/**
 * 获取线索意向分布数据
 */
export const getLeadIntentionDistribution = async (
  params?: LeadIntentionDistributionParams
): Promise<LeadIntentionDistributionData> => {
  const queryParams = new URLSearchParams();
  if (params?.start_date) queryParams.append('start_date', params.start_date);
  if (params?.end_date) queryParams.append('end_date', params.end_date);
  if (params?.assigned_to) queryParams.append('assigned_to', params.assigned_to.toString());
  if (params?.data_source) queryParams.append('data_source', params.data_source);
  if (params?.lead_type) queryParams.append('lead_type', params.lead_type);
  
  const url = `/statistics/lead-intention-distribution${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return await get<LeadIntentionDistributionData>(url);
};
